import React from 'react';
import Table from 'reactstrap/lib/Table';
import { ListGroup, ListGroupItem, ListGroupItemHeading, ListGroupItemText } from 'reactstrap';
import { IAriServiceAssemblyPartProductCombinations } from '../model-viewer';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { CommercePropertyValueEnum } from '../../../modules/allowable-freight/utils/CommercePropertyValueEnum';
import getExtensionPropertyValueByKey from '../../../utils/getExtensionPropertyValueByKey';
import AriRowComponent from './AriRow.component';

interface IAriPArtsListTableProps {
    parts: IAriServiceAssemblyPartProductCombinations[];
    tableHeadings: string[];
    baseClassName: String;
    goToId(id: string): void;
    addPartToCart(part: ProductSearchResult, partId: string, partQty: number): void;
    partIdPrefix: string;
    selectedRow: string | undefined;
    isAuthenticated: boolean;
    requiresAuthentication: boolean;
    loginToViewPricingText: string;
    unavailableText: string;
    addToCartText: string;
    signInUrl: string;
    isMobile: boolean;
    isDrawerOpen: boolean;
    closePartDrawer(): void;
    priceFormatter: (price: string | number, currencyCode?: string | undefined) => string;
    mfrCode: string;
    isDoNotAllowBackOrder: boolean;
    oosInvenLabel: string;
    contextNotePart: string;
    maxCharLimit: number;
    showMoreText: string;
    showLessText: string;
}

/**
 * Component to display a list of parts
 * @param props props
 */
export const AriPartsListTableComponent: React.FC<IAriPArtsListTableProps> = props => {
    const {
        tableHeadings,
        parts,
        baseClassName,
        goToId,
        addPartToCart,
        partIdPrefix,
        selectedRow,
        isAuthenticated,
        requiresAuthentication,
        loginToViewPricingText,
        signInUrl,
        isMobile,
        isDrawerOpen,
        unavailableText,
        addToCartText,
        closePartDrawer,
        priceFormatter,
        isDoNotAllowBackOrder,
        oosInvenLabel,
        contextNotePart,
        maxCharLimit,
        showLessText,
        showMoreText
    } = props;

    const [yPosition, setYPosition] = React.useState(0);
    const [expandedListRows, setExpandedListRows] = React.useState<Record<string, boolean>>({});
    const [expandedDrawerRows, setExpandedDrawerRows] = React.useState<Record<string, boolean>>({});
    const [quantities, setQuantities] = React.useState<Record<string, number>>({});

    React.useEffect(() => {
        //Scroll event handling
        window.removeEventListener('scroll', () => setYPosition(window.pageYOffset));
        window.addEventListener('scroll', () => setYPosition(window.pageYOffset), { passive: true });
        window.removeEventListener('scroll', () => setYPosition(window.pageYOffset));
        return;
    });

    const toggleListRow = (tag: string) => {
        setExpandedListRows(prev => ({
            ...prev,
            [tag]: !prev[tag]
        }));
    };

    const toggleDrawerRow = (tag: string) => {
        setExpandedDrawerRows(prev => ({
            ...prev,
            [tag]: !prev[tag]
        }));
    };

    const scrollDownClickHandler = (id: string) => {
        const element = document.getElementById(id);
        return window.scrollTo(0, element ? element.getBoundingClientRect().top : 0);
    };
    const selectedPartIdName = `${baseClassName}__part-selection-popover`;

    return (
        <>
            <div className={`${baseClassName}__list`}>
                {!isMobile ? (
                    <Table className={'list-view__table'}>
                        <thead>
                            <tr>
                                {tableHeadings.map((th, idx) => {
                                    if (!isAuthenticated && requiresAuthentication && (idx === 4 || idx === 5)) {
                                        return null;
                                    }

                                    return (
                                        <th key={th} className={`${baseClassName}__${idx}`}>
                                            {th}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {parts &&
                                parts.map(part => (
                                    <AriRowComponent
                                        key={part.PartId}
                                        part={part}
                                        baseClassName={baseClassName}
                                        partIdPrefix={partIdPrefix}
                                        contextNotePart={contextNotePart}
                                        maxCharLimit={maxCharLimit}
                                        isDoNotAllowBackOrder={isDoNotAllowBackOrder}
                                        selectedRow={selectedRow}
                                        isAuthenticated={isAuthenticated}
                                        requiresAuthentication={requiresAuthentication}
                                        loginToViewPricingText={loginToViewPricingText}
                                        signInUrl={signInUrl}
                                        unavailableText={unavailableText}
                                        addToCartText={addToCartText}
                                        priceFormatter={priceFormatter}
                                        oosInvenLabel={oosInvenLabel}
                                        goToId={goToId}
                                        addPartToCart={addPartToCart}
                                        toggleRow={toggleListRow}
                                        expandedRows={expandedListRows}
                                        isDrawer={false}
                                        showLessText={showLessText}
                                        showMoreText={showMoreText}
                                    />
                                ))}
                        </tbody>
                    </Table>
                ) : (
                    <ListGroup className='ari-model-viewer__parts-list'>
                        {parts &&
                            parts.map(part => {
                                const product = part.Product;
                                const tag = part.PartId!;
                                const full = `${contextNotePart} ${part.Context}`;
                                const isLong = full.length > maxCharLimit;
                                const isExpanded = Boolean(expandedListRows[tag]);
                                const shown = isLong && !isExpanded ? `${full.slice(0, maxCharLimit)}…` : full;
                                const partId = product && product.ItemId;
                                if (product?.ItemId === 'HOM538938002') {
                                    debugger;
                                }
                                const description = (product && product.Name) || part.Description;
                                const currentInvString =
                                    product &&
                                    getExtensionPropertyValueByKey(
                                        product.ExtensionProperties,
                                        'inventory',
                                        CommercePropertyValueEnum.string
                                    );
                                let currentInventory = parseInt(currentInvString as string, 10);
                                if (isNaN(currentInventory)) {
                                    currentInventory = 0;
                                }
                                const oosUpperBound = part.Product?.oosUpperBoundLimit || 0;
                                const canAddToCart =
                                    part.Product &&
                                    currentInventory > (isDoNotAllowBackOrder ? 0 : oosUpperBound) &&
                                    part.Product?.isEligible;
                                return (
                                    <ListGroupItem key={part.PartId} active>
                                        <ListGroupItemHeading className='ari-model-viewer__parts-list-heading'>
                                            <span className='ari-model-viewer__parts-list-heading--part-id'>
                                                {`${tableHeadings[0]}: `}
                                                {part.Tag}
                                            </span>
                                            <span className='ari-model-viewer__parts-list-heading--part-number'>
                                                {`${tableHeadings[1]}: `}
                                                {`${partId}`}
                                            </span>
                                        </ListGroupItemHeading>
                                        <ListGroupItemText>
                                            <span className='ari-model-viewer__parts-list-part-desc'>
                                                {`${tableHeadings[2]}: ${description}`}
                                                {part.Context && (
                                                    <span className={`${baseClassName}__context-note`}>
                                                        <br />
                                                        {isLong ? (
                                                            <>
                                                                {shown}{' '}
                                                                <button
                                                                    className={`${baseClassName}__context-note__toggle-button`}
                                                                    onClick={() => {
                                                                        toggleListRow(part.PartId!);
                                                                    }}
                                                                >
                                                                    {isExpanded ? showLessText : showMoreText}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <> {full}</>
                                                        )}
                                                    </span>
                                                )}
                                            </span>
                                        </ListGroupItemText>
                                        <ListGroupItemText>
                                            {isAuthenticated || !requiresAuthentication ? (
                                                <div className='ari-model-viewer__parts-list-item-details'>
                                                    <span className='ari-model-viewer__parts-list-item-details--part-price'>{`${
                                                        tableHeadings[4]
                                                    }: ${priceFormatter(part.Product?.BasePrice || 0)}`}</span>

                                                    <span className='ari-model-viewer__parts-list-item-details--part-price'>{`${
                                                        tableHeadings[5]
                                                    }: ${priceFormatter(part.Product?.Price || 0)}`}</span>
                                                </div>
                                            ) : (
                                                <a href={signInUrl}>{loginToViewPricingText}</a>
                                            )}
                                        </ListGroupItemText>
                                        <ListGroupItemText>
                                            {isAuthenticated || !requiresAuthentication ? (
                                                <div className='ari-model-viewer__parts-list-item-details'>
                                                    <span className='ari-model-viewer__parts-list-item-details--availability'>{`${tableHeadings[3]}: ${currentInventory}`}</span>
                                                    {canAddToCart && (
                                                        <span className='ari-model-viewer__parts-list-item-details--qty'>
                                                            {`${tableHeadings[6]}: `}
                                                            <input
                                                                className='quantity-input'
                                                                type='text'
                                                                min={1}
                                                                inputMode='numeric'
                                                                pattern='[0-9]*'
                                                                value={quantities[part.PartId!] ?? 1}
                                                                onChange={e => {
                                                                    const raw = e.target.value.replace(/\D/g, '');
                                                                    const parsed = parseInt(raw || '1', 10);
                                                                    const value = Math.max(1, parsed);
                                                                    setQuantities(prev => ({
                                                                        ...prev,
                                                                        [part.PartId!]: value
                                                                    }));
                                                                }}
                                                                disabled={!part.Product}
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                ''
                                            )}
                                        </ListGroupItemText>
                                        <ListGroupItemText>
                                            {isAuthenticated || !requiresAuthentication ? (
                                                <div className='ari-model-viewer__parts-list-item--add-to-cart'>
                                                    {canAddToCart ? (
                                                        <button
                                                            className={`${baseClassName}__ref-btn addtocart`}
                                                            onClick={() => {
                                                                const qty = quantities[part.PartId!] ?? 1;
                                                                addPartToCart(part.Product!, part.PartId!, qty);
                                                            }}
                                                        >
                                                            {addToCartText}
                                                        </button>
                                                    ) : (
                                                        <span className={`${baseClassName}__ref-btn addtocart disabled`}>
                                                            {isDoNotAllowBackOrder && oosUpperBound < 0 ? oosInvenLabel : unavailableText}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                ''
                                            )}
                                        </ListGroupItemText>
                                    </ListGroupItem>
                                );
                            })}
                    </ListGroup>
                )}
                {yPosition <= 0 && (
                    <div className='scroll-down-chevron' onClick={e => scrollDownClickHandler(`${baseClassName}__list`)}>
                        {/* scroll down */}
                    </div>
                )}
            </div>
            <div
                id={`${selectedPartIdName}`}
                className={`${selectedPartIdName} ${isDrawerOpen ? `${selectedPartIdName}--opened` : `${selectedPartIdName}--closed`}`}
            >
                <div className={`${selectedPartIdName}-table-wrapper`}>
                    <Table className={`${selectedPartIdName}-table`}>
                        <thead>
                            <tr>
                                {tableHeadings.map((th, idx) => {
                                    if (!isAuthenticated && requiresAuthentication && (idx === 4 || idx === 5)) {
                                        return null;
                                    }

                                    return (
                                        <th key={th} className={`${baseClassName}__${idx}`}>
                                            {th}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody id={`${selectedPartIdName}-table--body`}>
                            {selectedRow &&
                                parts
                                    .filter(p => p.Tag === selectedRow)
                                    .map(part => (
                                        <AriRowComponent
                                            key={part.PartId}
                                            part={part}
                                            baseClassName={baseClassName}
                                            partIdPrefix={partIdPrefix}
                                            contextNotePart={contextNotePart}
                                            maxCharLimit={maxCharLimit}
                                            isDoNotAllowBackOrder={isDoNotAllowBackOrder}
                                            selectedRow={selectedRow}
                                            isAuthenticated={isAuthenticated}
                                            requiresAuthentication={requiresAuthentication}
                                            loginToViewPricingText={loginToViewPricingText}
                                            signInUrl={signInUrl}
                                            unavailableText={unavailableText}
                                            addToCartText={addToCartText}
                                            priceFormatter={priceFormatter}
                                            oosInvenLabel={oosInvenLabel}
                                            goToId={goToId}
                                            addPartToCart={addPartToCart}
                                            toggleRow={toggleDrawerRow}
                                            expandedRows={expandedDrawerRows}
                                            isDrawer
                                            showLessText={showLessText}
                                            showMoreText={showMoreText}
                                        />
                                    ))}
                        </tbody>
                    </Table>
                    <div className={`${selectedPartIdName}-close-drawer`} onClick={closePartDrawer}>
                        X
                    </div>
                </div>
            </div>
        </>
    );
};
